export const ESTIMATE_DEFAULT_MS = 210_000;
export const ANCHOR_SKEW_LIMIT_MS = 60_000;

export interface PlayerPositionReport {
  requestId: string | null;
  positionMs: number;
  durationMs: number | null;
  isPlaying: boolean;
  reportedAt: string;
  title?: string;
  artist?: string;
}

export type CountdownSource = 'player' | 'estimate';

export type NowPlayingCountdown =
  | { kind: 'none' }
  | { kind: 'running'; remainingMs: number; source: CountdownSource; lengthKnown: boolean };

export function knownDurationMs(song: { durationMs?: number | null }): number | null {
  const duration = song.durationMs;
  return typeof duration === 'number' && duration > 0 ? duration : null;
}

export function parsePlayerPosition(raw: unknown): PlayerPositionReport | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (value.requestId !== null && typeof value.requestId !== 'string') return null;
  if (typeof value.positionMs !== 'number' || !Number.isFinite(value.positionMs)) return null;
  if (value.durationMs !== null && typeof value.durationMs !== 'number') return null;
  if (typeof value.isPlaying !== 'boolean' || typeof value.reportedAt !== 'string') return null;
  return {
    requestId: value.requestId,
    positionMs: value.positionMs,
    durationMs: value.durationMs,
    isPlaying: value.isPlaying,
    reportedAt: value.reportedAt,
    ...(typeof value.title === 'string' && value.title.trim() !== '' ? { title: value.title } : {}),
    ...(typeof value.artist === 'string' && value.artist.trim() !== '' ? { artist: value.artist } : {}),
  };
}

export function positionAnchorMs(reportedAt: string, receivedAtMs: number): number {
  const reportedMs = Date.parse(reportedAt);
  if (Number.isNaN(reportedMs)) return receivedAtMs;
  return Math.abs(receivedAtMs - reportedMs) <= ANCHOR_SKEW_LIMIT_MS ? reportedMs : receivedAtMs;
}

export function resolveNowPlayingCountdown(input: {
  level: 'bridge' | 'checklist';
  nowPlaying: {
    id: string;
    durationMs?: number | null;
    startedPlaying?: string | null;
    isLocalFile: boolean;
    manualPick?: boolean;
    awaitingOffTrackChoice?: boolean;
  } | null;
  position: PlayerPositionReport | null;
  positionAnchorMs: number | null;
  estimatePausedAtMs: number | null;
  estimatePauseOffsetMs: number;
  nowMs: number;
}): NowPlayingCountdown {
  const { level, nowPlaying, position, positionAnchorMs: anchorMs, estimatePausedAtMs, estimatePauseOffsetMs, nowMs } = input;
  if (level === 'checklist' || !nowPlaying || nowPlaying.isLocalFile) return { kind: 'none' };
  // The song Bridge says the player moved on from can't be trusted while the DJ decides what's playing (ITER-173)
  if (nowPlaying.awaitingOffTrackChoice) return { kind: 'none' };

  const requestLengthMs = knownDurationMs(nowPlaying);

  if (position && position.requestId === nowPlaying.id && anchorMs !== null) {
    const playerLengthMs = position.durationMs !== null && position.durationMs > 0 ? position.durationMs : null;
    const lengthMs = playerLengthMs ?? requestLengthMs;
    if (lengthMs !== null) {
      const elapsedMs = position.positionMs + (position.isPlaying ? Math.max(0, nowMs - anchorMs) : 0);
      return { kind: 'running', remainingMs: Math.max(0, lengthMs - elapsedMs), source: 'player', lengthKnown: true };
    }
  }

  // A hand pick follows the player's own clock, never the saved length (ITER-182)
  if (nowPlaying.manualPick) return { kind: 'none' };

  if (!nowPlaying.startedPlaying) return { kind: 'none' };
  const startedAtMs = Date.parse(nowPlaying.startedPlaying);
  if (Number.isNaN(startedAtMs)) return { kind: 'none' };

  const lengthMs = requestLengthMs ?? ESTIMATE_DEFAULT_MS;
  const clockMs = estimatePausedAtMs ?? nowMs;
  const remainingMs = Math.max(0, lengthMs - (clockMs - startedAtMs - estimatePauseOffsetMs));
  return { kind: 'running', remainingMs, source: 'estimate', lengthKnown: requestLengthMs !== null };
}
